import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Collapse,
  TablePagination,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarViewMonth as CalendarViewMonthIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { addDays, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import EventForm from "../../components/admin/EventForm";
import EventEditModal from "../../components/admin/EventEditModal";
import BotonRecibo from "../../components/BotonRecibo";
import BotonContrato from "../../components/BotonContrato";
import MobileDataCard, {
  MobileDataList,
} from "../../components/common/MobileDataCard";
import CalendarDisponibilidad from "../../components/common/CalendarDisponibilidad";
import api from "../../services/api";
import {
  formatDateShort,
  formatDateLong,
  parseFechaLocal,
  getCurrentMonth,
  getCurrentYear,
} from "../../utils/dateUtils";

/**
 * AdminEventos - Página para gestionar eventos
 * Listado principal con opciones de crear, editar, eliminar y ver detalles
 */
const AdminEventos = () => {
  // Detección de móvil para vista responsiva
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Estados principales
  const [eventos, setEventos] = useState([]);
  const [filteredEventos, setFilteredEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados para filtros de fecha
  const [dateFilterTab, setDateFilterTab] = useState(0); // 0: 8 días, 1: 15 días, 2: mes
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth() - 1); // 0-11 (getCurrentMonth devuelve 1-12)
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());

  // Lista de meses para el selector
  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  // Estados para formulario
  const [formMode, setFormMode] = useState(null); // null, 'create', 'edit'
  const [selectedEventoId, setSelectedEventoId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteEventoId, setDeleteEventoId] = useState(null);

  // Estado para edición inline en vista expandida
  const [editingEvento, setEditingEvento] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  const [savingInline, setSavingInline] = useState(false);

  // Estado para modal de edición profesional
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEventoForEdit, setSelectedEventoForEdit] = useState(null);

  // Vista: lista o calendario de disponibilidad
  const [vistaCalendario, setVistaCalendario] = useState(false);

  // Cargar eventos al montar
  useEffect(() => {
    // Verificar si estamos siendo redirigidos a login (para evitar loops)
    const isRedirecting = sessionStorage.getItem("redirecting_to_login");
    if (!isRedirecting) {
      loadEventos();
      loadColaboradores();
    } else {
      // El flag quedó stale (login exitoso sin limpiar el flag).
      // Limpiarlo aquí y cargar normalmente.
      sessionStorage.removeItem("redirecting_to_login");
      loadEventos();
      loadColaboradores();
    }
  }, []);

  // Filtrar eventos cuando cambian filtros
  useEffect(() => {
    filterEventos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventos, searchTerm, dateFilterTab, selectedMonth, selectedYear]);

  const loadColaboradores = async () => {
    try {
      const response = await api.get("/colaboradores");
      const lista = response.data?.colaboradores || response.data?.data || [];
      setColaboradores(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error("[AdminEventos] Error cargando colaboradores:", err);
    }
  };

  const loadEventos = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/eventos?include=cliente,colaboradores");
      // La API devuelve {eventos: [...]} dentro de response.data
      const lista = response.data?.eventos || response.data?.data || [];
      setEventos(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error("[AdminEventos] Error:", err);

      // Si es 401, NO mostrar error - está siendo manejado por el interceptor
      if (err.response?.status !== 401) {
        setError("Error al cargar los eventos");
        setEventos([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterEventos = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered = [...eventos];

    // Aplicar filtro de fecha según la pestaña seleccionada
    if (dateFilterTab === 0) {
      // Próximos 8 días
      const endDate = addDays(today, 8);
      filtered = filtered.filter((evento) => {
        const fechaEvento = parseFechaLocal(
          evento.fecha_evento || evento.fecha_inicio,
        );
        if (!fechaEvento) return false;
        return fechaEvento >= today && fechaEvento <= endDate;
      });
    } else if (dateFilterTab === 1) {
      // Próximos 15 días
      const endDate = addDays(today, 15);
      filtered = filtered.filter((evento) => {
        const fechaEvento = parseFechaLocal(
          evento.fecha_evento || evento.fecha_inicio,
        );
        if (!fechaEvento) return false;
        return fechaEvento >= today && fechaEvento <= endDate;
      });
    } else if (dateFilterTab === 2) {
      // Por mes seleccionado
      const monthStart = startOfMonth(new Date(selectedYear, selectedMonth, 1));
      const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth, 1));
      filtered = filtered.filter((evento) => {
        const fechaEvento = parseFechaLocal(
          evento.fecha_evento || evento.fecha_inicio,
        );
        if (!fechaEvento) return false;
        return isWithinInterval(fechaEvento, {
          start: monthStart,
          end: monthEnd,
        });
      });
    }
    // Tab 3: Todos - no se filtra por fecha

    // Aplicar filtro de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((evento) => {
        return (
          evento.titulo?.toLowerCase().includes(term) ||
          evento.cliente_nombre?.toLowerCase().includes(term) ||
          evento.ubicacion?.toLowerCase().includes(term) ||
          evento.estado?.toLowerCase().includes(term)
        );
      });
    }

    // Ordenar por fecha
    filtered.sort((a, b) => {
      const fechaA = parseFechaLocal(a.fecha_evento || a.fecha_inicio);
      const fechaB = parseFechaLocal(b.fecha_evento || b.fecha_inicio);
      return fechaA - fechaB;
    });

    setFilteredEventos(filtered);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteEventoId) return;

    try {
      await api.delete(`/eventos/${deleteEventoId}`);
      setEventos(eventos.filter((e) => e.id !== deleteEventoId));
      setSuccess("Evento eliminado correctamente");
      setDeleteConfirmOpen(false);
      setDeleteEventoId(null);

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("[AdminEventos] Error eliminando:", err);
      setError("Error al eliminar el evento");
    }
  };

  const handleFormSuccess = (updatedEvento) => {
    const msg = formMode === "create" ? "Evento creado correctamente" : "Evento actualizado correctamente";
    setFormMode(null);
    setSelectedEventoId(null);
    setSuccess(msg);
    loadEventos();
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleFormCancel = () => {
    setFormMode(null);
    setSelectedEventoId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      Presupuesto: "default",
      Confirmado: "primary",
      "En curso": "info",
      Completado: "success",
      Cancelado: "error",
    };
    return colors[estado] || "default";
  };

  const getDisplayedEventos = () => {
    return filteredEventos.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  };

  // Iniciar edición inline
  const handleStartInlineEdit = (evento) => {
    setEditingEvento({
      ...evento,
      fecha_evento: evento.fecha_evento || evento.fecha_inicio || "",
      cubierto_por_id: evento.cubierto_por_id || "",
    });
  };

  // Cancelar edición inline
  const handleCancelInlineEdit = () => {
    setEditingEvento(null);
  };

  // Guardar edición inline
  const handleSaveInlineEdit = async () => {
    if (!editingEvento) return;

    try {
      setSavingInline(true);
      await api.put(`/eventos/${editingEvento.id}`, {
        titulo: editingEvento.titulo,
        descripcion: editingEvento.descripcion,
        fecha_inicio: editingEvento.fecha_evento,
        ubicacion: editingEvento.ubicacion,
        direccion: editingEvento.direccion,
        cliente_id: editingEvento.cliente_id,
        estado: editingEvento.estado,
        cubierto_por_id: editingEvento.cubierto_por_id || null,
        monto_paquete: editingEvento.costo_servicio || 0,
        adelanto: editingEvento.anticipo || 0,
      });

      // Actualizar lista local
      setEventos(
        eventos.map((e) =>
          e.id === editingEvento.id ? { ...e, ...editingEvento } : e,
        ),
      );
      setEditingEvento(null);
      setSuccess("Evento actualizado correctamente");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("[AdminEventos] Error guardando:", err);
      setError("Error al guardar los cambios");
    } finally {
      setSavingInline(false);
    }
  };

  // Actualizar campo de edición
  const handleInlineChange = (field, value) => {
    setEditingEvento((prev) => ({ ...prev, [field]: value }));
  };

  // Abrir modal de edición
  const handleOpenEditModal = (evento) => {
    setSelectedEventoForEdit(evento);
    setEditModalOpen(true);
  };

  // Guardar desde modal de edición
  const handleEditModalSave = (updatedEvento) => {
    setEventos(
      eventos.map((e) =>
        e.id === updatedEvento.id ? { ...e, ...updatedEvento } : e,
      ),
    );
    setSuccess("Evento actualizado correctamente");
    setTimeout(() => setSuccess(""), 3000);
  };

  // Renderizar formulario en modal
  if (formMode) {
    return (
      <Box>
        <EventForm
          eventoId={formMode === "edit" ? selectedEventoId : null}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Box>
    );
  }

  // Renderizar listado principal
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Encabezado */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Gestión de Eventos
        </Typography>
        {/* Controles de vista + crear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Toggle lista / disponibilidad */}
          <Box sx={{ display: "flex", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", overflow: "hidden" }}>
            <Tooltip title="Vista lista">
              <IconButton
                size="small"
                onClick={() => setVistaCalendario(false)}
                sx={{
                  borderRadius: 0,
                  px: 1.2, py: 0.8,
                  bgcolor: !vistaCalendario ? "rgba(99,102,241,0.25)" : "transparent",
                  color: !vistaCalendario ? "#a5b4fc" : "text.secondary",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.15)" },
                }}
              >
                <ViewListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Disponibilidad de fechas">
              <IconButton
                size="small"
                onClick={() => setVistaCalendario(true)}
                sx={{
                  borderRadius: 0,
                  px: 1.2, py: 0.8,
                  bgcolor: vistaCalendario ? "rgba(99,102,241,0.25)" : "transparent",
                  color: vistaCalendario ? "#a5b4fc" : "text.secondary",
                  "&:hover": { bgcolor: "rgba(99,102,241,0.15)" },
                }}
              >
                <CalendarViewMonthIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Botón crear evento (oculto en vista calendario en móvil) */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { setFormMode("create"); setSelectedEventoId(null); }}
            sx={{ display: { xs: "none", sm: "flex" } }}
          >
            Crear Evento
          </Button>
          <IconButton
            onClick={() => { setFormMode("create"); setSelectedEventoId(null); }}
            sx={{
              display: { xs: "flex", sm: "none" },
              width: 40, height: 40,
              background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
              color: "#fff",
              boxShadow: "0 3px 8px rgba(25,118,210,.4)",
              "&:hover": { background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)" },
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* ── VISTA CALENDARIO DE DISPONIBILIDAD ─────────────────────────────── */}
      {vistaCalendario && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CalendarViewMonthIcon sx={{ color: "#6366f1", fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.15rem" } }}>
              Disponibilidad de Fechas
            </Typography>
            <Chip label="Admin" size="small"
              sx={{ ml: "auto", height: 20, fontSize: "0.6rem", bgcolor: "rgba(99,102,241,0.15)", color: "#a5b4fc" }} />
          </Box>
          <CalendarDisponibilidad
            userRole="admin"
            onCreateEvent={(fechaStr) => {
              setFormMode("create");
              setSelectedEventoId(null);
              setVistaCalendario(false);
            }}
          />
        </Paper>
      )}

      {/* Ocultar lista cuando está activo el calendario */}
      {vistaCalendario ? null : (
        <>

      {/* Buscador */}
      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por nombre, cliente, ubicación o estado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
          }}
        />
      </Paper>

      {/* Filtros de Fecha - Pestañas */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={dateFilterTab}
          onChange={(e, newValue) => {
            setDateFilterTab(newValue);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Próximos 8 días" />
          <Tab label="Próximos 15 días" />
          <Tab label="Por Mes" />
          <Tab label="Todos" />
        </Tabs>

        {/* Selector de Mes (solo visible cuando la pestaña "Por Mes" está activa) */}
        {dateFilterTab === 2 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              bgcolor: "background.default",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={selectedMonth}
                label="Mes"
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setPage(0);
                }}
              >
                {meses.map((mes, index) => (
                  <MenuItem key={index} value={index}>
                    {mes}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={selectedYear}
                label="Año"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setPage(0);
                }}
              >
                {[2024, 2025, 2026, 2027, 2028].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: "auto" }}
            >
              {filteredEventos.length} evento(s) en {meses[selectedMonth]}{" "}
              {selectedYear}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Vista de datos */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* ═══════════════════════════════════════════════════════════════════
           VISTA MÓVIL - Cards expandibles
           ═══════════════════════════════════════════════════════════════════ */
        <Box>
          <MobileDataList
            loading={loading}
            emptyMessage={`No hay eventos en los próximos ${dateFilterTab === 0 ? "8" : dateFilterTab === 1 ? "15" : ""} días`}
          >
            {getDisplayedEventos().map((evento, index) => {
              const estadoColors = {
                Pendiente: "#ffc107",
                Confirmado: "#2196f3",
                "En curso": "#9c27b0",
                Completado: "#4caf50",
                Cancelado: "#f44336",
              };

              return (
                <MobileDataCard
                  key={evento.id}
                  title={evento.titulo || evento.nombre || "Sin nombre"}
                  subtitle={
                    evento.cliente_nombre || evento.cliente?.nombre_completo
                  }
                  avatarColor={estadoColors[evento.estado] || "#1976d2"}
                  status={{
                    label: evento.estado || "Pendiente",
                    color: estadoColors[evento.estado] || "#757575",
                  }}
                  primaryInfo={[
                    {
                      icon: <CalendarIcon fontSize="small" />,
                      label: "Fecha",
                      value:
                        evento.fecha_evento || evento.fecha_inicio
                          ? formatDateShort(
                              evento.fecha_evento || evento.fecha_inicio,
                            )
                          : "-",
                    },
                    {
                      icon: <TimeIcon fontSize="small" />,
                      label: "Hora",
                      value: evento.hora_inicio || "-",
                    },
                  ]}
                  expandedInfo={[
                    {
                      icon: <LocationIcon fontSize="small" />,
                      label: "Ubicación",
                      value: evento.ubicacion || evento.direccion || "-",
                    },
                    {
                      icon: <TimeIcon fontSize="small" />,
                      label: "Duración",
                      value: evento.duracion_horas
                        ? `${evento.duracion_horas} hrs`
                        : "-",
                    },
                    {
                      icon: <MoneyIcon fontSize="small" />,
                      label: "Costo",
                      value: evento.costo_servicio
                        ? `$${Number(evento.costo_servicio).toLocaleString("es-MX")}`
                        : "-",
                    },
                    {
                      icon: <MoneyIcon fontSize="small" />,
                      label: "Anticipo",
                      value: evento.anticipo
                        ? `$${Number(evento.anticipo).toLocaleString("es-MX")}`
                        : "$0",
                    },
                    {
                      icon: <PersonIcon fontSize="small" />,
                      label: "Colaborador",
                      value: evento.colaborador_nombre || "-",
                    },
                  ]}
                  actions={[
                    {
                      icon: <EditIcon fontSize="small" />,
                      label: "Editar",
                      onClick: () => handleOpenEditModal(evento),
                    },
                    {
                      icon: <DeleteIcon fontSize="small" />,
                      label: "Eliminar",
                      color: "error",
                      onClick: () => {
                        setDeleteEventoId(evento.id);
                        setDeleteConfirmOpen(true);
                      },
                    },
                  ]}
                  customActions={[
                    <BotonContrato key="contract" evento={evento} variant="icon" size="small" />,
                  ]}
                />
              );
            })}
          </MobileDataList>

          {/* Paginación móvil */}
          {filteredEventos.length > 0 && (
            <Box sx={{ px: 2, pb: 2 }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredEventos.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} de ${count}`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    justifyContent: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    display: { xs: "none", sm: "block" },
                  },
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        /* ═══════════════════════════════════════════════════════════════════
           VISTA DESKTOP - Tabla tradicional
           ═══════════════════════════════════════════════════════════════════ */
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "primary.main" }}>
              <TableRow sx={{ "& th": { color: "white", fontWeight: "bold" } }}>
                <TableCell align="center" sx={{ width: 50 }}>
                  #
                </TableCell>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Evento</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Horas</TableCell>
                <TableCell>Ubicación</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getDisplayedEventos().map((evento, index) => (
                <React.Fragment key={evento.id}>
                  {/* Fila principal */}
                  <TableRow
                    sx={{
                      "&:hover": { bgcolor: "rgba(33, 150, 243, 0.08)" },
                      cursor: "pointer",
                      bgcolor:
                        expandedRow === evento.id
                          ? "rgba(33, 150, 243, 0.12)"
                          : "inherit",
                    }}
                  >
                    {/* Número de evento */}
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                      >
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>

                    {/* Expandir */}
                    <TableCell
                      onClick={() =>
                        setExpandedRow(
                          expandedRow === evento.id ? null : evento.id,
                        )
                      }
                    >
                      <IconButton size="small">
                        {expandedRow === evento.id ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>

                    {/* Nombre */}
                    <TableCell>{evento.titulo || evento.nombre}</TableCell>

                    {/* Cliente */}
                    <TableCell>
                      {evento.cliente_nombre || evento.cliente?.nombre_completo}
                    </TableCell>

                    {/* Fecha - usando formatDateShort para zona horaria correcta */}
                    <TableCell>
                      {evento.fecha_evento || evento.fecha_inicio
                        ? formatDateShort(
                            evento.fecha_evento || evento.fecha_inicio,
                          )
                        : "-"}
                    </TableCell>

                    {/* Horas Contratadas */}
                    <TableCell align="center">
                      {evento.duracion_horas
                        ? `${evento.duracion_horas}h`
                        : "-"}
                    </TableCell>

                    {/* Ubicación */}
                    <TableCell>{evento.ubicacion}</TableCell>

                    {/* Estado */}
                    <TableCell>
                      <Chip
                        label={evento.estado || "Sin estado"}
                        color={getEstadoColor(evento.estado)}
                        size="small"
                      />
                    </TableCell>

                    {/* Acciones */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditModal(evento)}
                        title="Editar evento"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeleteEventoId(evento.id);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Fila expandida - Vista detallada editable */}
                  {expandedRow === evento.id && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, bgcolor: "grey.50" }}>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3 }}>
                            {/* Header con botones */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  color: "text.primary",
                                  fontWeight: "bold",
                                }}
                              >
                                Detalles del Evento
                              </Typography>
                              <Box>
                                {editingEvento?.id === evento.id ? (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<CloseIcon />}
                                      onClick={handleCancelInlineEdit}
                                      sx={{ mr: 1 }}
                                    >
                                      Cancelar
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      startIcon={<SaveIcon />}
                                      onClick={handleSaveInlineEdit}
                                      disabled={savingInline}
                                    >
                                      {savingInline
                                        ? "Guardando..."
                                        : "Guardar"}
                                    </Button>
                                  </>
                                ) : (
                                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <BotonRecibo evento={evento} size="small" />
                                    <BotonContrato evento={evento} size="small" />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<EditIcon />}
                                      onClick={() =>
                                        handleStartInlineEdit(evento)
                                      }
                                    >
                                      Editar
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* Contenido editable */}
                            <Grid container spacing={3}>
                              {/* Columna izquierda */}
                              <Grid item xs={12} md={6}>
                                <Paper
                                  sx={{ p: 2, bgcolor: "background.paper" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      color: "primary.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Información General
                                  </Typography>

                                  {/* Título */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Título del Evento
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={editingEvento.titulo || ""}
                                        onChange={(e) =>
                                          handleInlineChange(
                                            "titulo",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {evento.titulo || "-"}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Descripción */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Descripción
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        rows={2}
                                        value={editingEvento.descripcion || ""}
                                        onChange={(e) =>
                                          handleInlineChange(
                                            "descripcion",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Typography variant="body1">
                                        {evento.descripcion || "-"}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Fecha */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Fecha del Evento
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={editingEvento.fecha_evento || ""}
                                        onChange={(e) =>
                                          handleInlineChange(
                                            "fecha_evento",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Typography variant="body1">
                                        {evento.fecha_evento ||
                                        evento.fecha_inicio
                                          ? formatDateLong(
                                              evento.fecha_evento ||
                                                evento.fecha_inicio,
                                            )
                                          : "-"}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Estado */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Estado
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <FormControl fullWidth size="small">
                                        <Select
                                          value={
                                            editingEvento.estado ||
                                            "Por Confirmar"
                                          }
                                          onChange={(e) =>
                                            handleInlineChange(
                                              "estado",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <MenuItem value="Por Confirmar">
                                            Por Confirmar
                                          </MenuItem>
                                          <MenuItem value="Confirmado">
                                            Confirmado
                                          </MenuItem>
                                          <MenuItem value="En curso">
                                            En curso
                                          </MenuItem>
                                          <MenuItem value="Completado">
                                            Completado
                                          </MenuItem>
                                          <MenuItem value="Cancelado">
                                            Cancelado
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <Box>
                                        <Chip
                                          label={evento.estado || "Sin estado"}
                                          color={getEstadoColor(evento.estado)}
                                          size="small"
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </Paper>
                              </Grid>

                              {/* Columna derecha */}
                              <Grid item xs={12} md={6}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    bgcolor: "background.paper",
                                    mb: 2,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      color: "primary.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Ubicación
                                  </Typography>

                                  {/* Ubicación */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Ubicación
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={editingEvento.ubicacion || ""}
                                        onChange={(e) =>
                                          handleInlineChange(
                                            "ubicacion",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Typography variant="body1">
                                        {evento.ubicacion || "-"}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Dirección */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Dirección completa
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <TextField
                                        fullWidth
                                        size="small"
                                        value={editingEvento.direccion || ""}
                                        onChange={(e) =>
                                          handleInlineChange(
                                            "direccion",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    ) : (
                                      <Typography variant="body1">
                                        {evento.direccion || "-"}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>

                                <Paper
                                  sx={{ p: 2, bgcolor: "background.paper" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      color: "primary.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Colaboradores que cubrirán el evento
                                  </Typography>

                                  {/* Cubierto por */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Colaborador asignado
                                    </Typography>
                                    {editingEvento?.id === evento.id ? (
                                      <FormControl fullWidth size="small">
                                        <Select
                                          value={
                                            editingEvento.cubierto_por_id || ""
                                          }
                                          onChange={(e) =>
                                            handleInlineChange(
                                              "cubierto_por_id",
                                              e.target.value,
                                            )
                                          }
                                          displayEmpty
                                        >
                                          <MenuItem value="">
                                            Sin asignar
                                          </MenuItem>
                                          {colaboradores.map((col) => (
                                            <MenuItem
                                              key={col.id}
                                              value={col.id}
                                            >
                                              {col.nombre_completo}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <Typography variant="body1">
                                        {evento.cubierto_por_nombre ||
                                          colaboradores.find(
                                            (c) =>
                                              c.id === evento.cubierto_por_id,
                                          )?.nombre_completo ||
                                          "Sin asignar"}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              </Grid>

                              {/* Fila completa - Información Financiera */}
                              <Grid item xs={12}>
                                <Paper
                                  sx={{ p: 2, bgcolor: "background.paper" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      color: "primary.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Información Financiera
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Monto Total
                                      </Typography>
                                      {editingEvento?.id === evento.id ? (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          value={
                                            editingEvento.costo_servicio || 0
                                          }
                                          onChange={(e) =>
                                            handleInlineChange(
                                              "costo_servicio",
                                              parseFloat(e.target.value) || 0,
                                            )
                                          }
                                          InputProps={{ startAdornment: "$" }}
                                        />
                                      ) : (
                                        <Typography
                                          variant="h6"
                                          sx={{ color: "success.main" }}
                                        >
                                          $
                                          {(
                                            evento.costo_servicio ||
                                            evento.monto_paquete ||
                                            0
                                          ).toLocaleString("es-MX")}
                                        </Typography>
                                      )}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Anticipo
                                      </Typography>
                                      {editingEvento?.id === evento.id ? (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          value={editingEvento.anticipo || 0}
                                          onChange={(e) =>
                                            handleInlineChange(
                                              "anticipo",
                                              parseFloat(e.target.value) || 0,
                                            )
                                          }
                                          InputProps={{ startAdornment: "$" }}
                                        />
                                      ) : (
                                        <Typography
                                          variant="h6"
                                          sx={{ color: "info.main" }}
                                        >
                                          $
                                          {(
                                            evento.anticipo ||
                                            evento.adelanto ||
                                            0
                                          ).toLocaleString("es-MX")}
                                        </Typography>
                                      )}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Pendiente
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{ color: "warning.main" }}
                                      >
                                        $
                                        {(
                                          (editingEvento?.id === evento.id
                                            ? editingEvento.costo_servicio
                                            : evento.costo_servicio || 0) -
                                          (editingEvento?.id === evento.id
                                            ? editingEvento.anticipo
                                            : evento.anticipo || 0)
                                        ).toLocaleString("es-MX")}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </Grid>

                              {/* Cliente */}
                              <Grid item xs={12}>
                                <Paper
                                  sx={{ p: 2, bgcolor: "background.paper" }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      mb: 2,
                                      color: "primary.main",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Cliente
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Nombre
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        {evento.cliente_nombre || "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Teléfono
                                      </Typography>
                                      <Typography variant="body1">
                                        {evento.cliente_telefono || "-"}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Canal
                                      </Typography>
                                      <Typography variant="body1">
                                        {evento.canal || "-"}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}

              {getDisplayedEventos().length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">
                      {dateFilterTab === 0
                        ? "No hay eventos en los próximos 8 días"
                        : dateFilterTab === 1
                          ? "No hay eventos en los próximos 15 días"
                          : `No hay eventos en ${meses[selectedMonth]} ${selectedYear}`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Paginación */}
          {filteredEventos.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredEventos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Eventos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
            />
          )}
        </TableContainer>
      )}

        </> /* fin fragmento lista (vistaCalendario ? null : <>...</>) */
      )}

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar este evento? Esta acción no se
            puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edición profesional */}
      <EventEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedEventoForEdit(null);
        }}
        evento={selectedEventoForEdit}
        colaboradores={colaboradores}
        onSave={handleEditModalSave}
      />
    </Box>
  );
};

export default AdminEventos;
